import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ExpandableText.css";

type ExpandableTextProps = {
  text: string;
  maxLines?: number;
  className?: string;
  showMoreLabel?: string;
  showLessLabel?: string;
};

const ExpandableText = ({ text, maxLines = 3, className, showMoreLabel, showLessLabel }: ExpandableTextProps) => {
  const { t } = useTranslation("components");
  const bodyRef = useRef<HTMLParagraphElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setOverflowing(false);
  }, [text]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const measure = () => {
      if (!bodyRef.current || expanded) return;
      const node = bodyRef.current;
      setOverflowing(node.scrollHeight > node.clientHeight + 1);
    };

    measure();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, maxLines, expanded]);

  const bodyClass = `expandable-text__body${expanded ? " expandable-text__body--expanded" : ""}${className ? ` ${className}` : ""}`;
  const bodyStyle = !expanded ? ({ "--expandable-max-lines": maxLines } as CSSProperties) : undefined;
  const showToggle = overflowing || expanded;

  return (
    <div className="expandable-text">
      <p ref={bodyRef} className={bodyClass} style={bodyStyle}>
        {text}
      </p>
      {showToggle && (
        <button
          type="button"
          className="expandable-text__toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? (showLessLabel ?? t("expandableText.showLess")) : (showMoreLabel ?? t("expandableText.showMore"))}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
